import { useEffect, useState } from 'react'
import TextField from '@mui/material/TextField'
import styles from './styles.module.css'
import {
  IInstallationPoint,
  InstallationPoint,
} from '../../../../../../../../app/entities/InstallationPoint'
import { ICustomerVehicle } from '../../../../../../../../app/entities/CustomerVehicle'
import { MODES } from '../../../../../../global/constants/scheduler'
import { Box, Modal } from '@mui/material'
import SecondaryButton from '../../../../../../global/components/buttons/secondary-button/SecondaryButton'
import PrimaryButton from '../../../../../../global/components/buttons/primary-button/PrimaryButton'
import GoogleAddressSearchBar from '../../../../../../global/components/google-address-search/GoogleAddressSearchBar'
import { ILocation } from '../../../../../../../../app/entities/Location'
import { fetchData } from '../../../../../../global/helper-functions/fetch'
import { updateInstallationPoint } from '../api'
import { getDirections } from '../../appointment-slot/api'

async function setInstallationPointValuesFromGoogleAddress(
  installationPoint: Partial<InstallationPoint>,
  setInstallationPoint: Function,
  chosenAddress: google.maps.GeocoderResult | null | undefined,
  location: ILocation | undefined,
) {
  if (chosenAddress) {
    const { address_components } = chosenAddress
    const [zipCode, state, city, streetNumber, streetName] = [
      'postal_code',
      'administrative_area_level_1',
      'locality',
      'street_number',
      'route',
    ].map((type) =>
      address_components.find((component) => component.types.includes(type)),
    )

    const installationPointValuesFromGoogle: Partial<InstallationPoint> = {}
    installationPointValuesFromGoogle.zipCode = zipCode?.long_name
    installationPointValuesFromGoogle.state = state?.short_name
    installationPointValuesFromGoogle.city = city?.long_name
    installationPointValuesFromGoogle.address = `${streetNumber?.long_name} ${streetName?.long_name}`

    const response = await fetchData<{
      status: google.maps.GeocoderStatus
      geocoded_waypoints: google.maps.DirectionsGeocodedWaypoint[]
      routes: google.maps.DirectionsRoute[]
    }>(getDirections(location?.objectId || '', chosenAddress?.place_id || ''))
    const drivingDetails = response.routes[0].legs[0]
    if (Object.keys(installationPointValuesFromGoogle).length > 0) {
      setInstallationPoint({
        ...installationPoint,
        drivingDetails,
        ...installationPointValuesFromGoogle,
      })
    }
  }
}

interface IInstallationPointProps {
  installationPoint: Partial<IInstallationPoint>
  setInstallationPoint: Function
  customerVehicle: Partial<ICustomerVehicle>
  setCustomerVehicle: Function
  initialChosenAddress: google.maps.GeocoderResult | null | undefined
  mode: string
  location: ILocation | undefined
}

export default function InstallationAddressForm({
  installationPoint,
  setInstallationPoint,
  mode,
  location,
  initialChosenAddress,
}: IInstallationPointProps) {
  const [isChangeAddressModalOpen, setChangeAddressModalOpen] = useState(false)
  const [isZipCodeServiced, setIsZipCodeServiced] = useState<any>(undefined)
  const [chosenAddress, setChosenAddress] =
    useState<google.maps.GeocoderResult | null>(null)
  const [addressInput, setAddressInput] = useState('')

  useEffect(() => {
    if (initialChosenAddress) {
      const { address_components } = initialChosenAddress
      const [zipCode, state, city, streetNumber, streetName] = [
        'postal_code',
        'administrative_area_level_1',
        'locality',
        'street_number',
        'route',
      ].map((type) =>
        address_components.find((component) => component.types.includes(type)),
      )

      const installationPointValuesFromGoogle: Partial<InstallationPoint> = {}
      installationPointValuesFromGoogle.zipCode = zipCode?.long_name
      installationPointValuesFromGoogle.state = state?.short_name
      installationPointValuesFromGoogle.city = city?.long_name
      installationPointValuesFromGoogle.address = `${streetNumber?.long_name} ${streetName?.long_name}`

      const existsGoogleAddress =
        Object.keys(installationPointValuesFromGoogle).length > 0

      if (existsGoogleAddress) {
        setInstallationPoint({
          ...installationPoint,
          ...installationPointValuesFromGoogle,
        })
      }
    }
  }, [initialChosenAddress])

  async function handleEditAddress() {
    try {
      await setInstallationPointValuesFromGoogleAddress(
        installationPoint,
        setInstallationPoint,
        chosenAddress,
        location,
      )
    } catch (error: any) {
      alert(
        'Something is wrong with the address. Please try a variation of the address instead.',
      )
      throw new Error(error)
    } finally {
      handleClose()
    }
    return
  }

  function handleClose() {
    setChangeAddressModalOpen(false)
    return
  }

  function handleOpenModal() {
    setChangeAddressModalOpen(true)
    return
  }

  function primaryButtonNameHandler() {
    if (!chosenAddress) return 'Start typing to select an address'
    if (isZipCodeServiced === undefined) return 'Checking zip code...'
    if (!isZipCodeServiced) return 'Zip code not serviced'
    return 'Update Address'
  }

  return (
    <div>
      <Modal open={isChangeAddressModalOpen}>
        <Box className={styles.modal}>
          <div className={`${styles.modalPageTitle} font--bold`}>
            {`Edit Address`}
          </div>
          <GoogleAddressSearchBar
            addressInput={addressInput}
            setAddressInput={setAddressInput}
            setIsZipCodeServiced={setIsZipCodeServiced}
            location={location}
            setChosenAddress={setChosenAddress}
          />
          <div style={{ width: 500, height: 300 }}>
            {chosenAddress && (
              <iframe
                width='500'
                height='300'
                style={{ border: 0 }}
                referrerPolicy='no-referrer-when-downgrade'
                src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=place_id:${chosenAddress.place_id}`}
              ></iframe>
            )}
          </div>

          <div className={styles.buttonContainer}>
            <SecondaryButton buttonName='Cancel' onClick={handleClose} />
            <PrimaryButton
              buttonName={primaryButtonNameHandler()}
              disabled={!isZipCodeServiced || !chosenAddress}
              onClick={handleEditAddress}
            />
          </div>
        </Box>
      </Modal>
      <div className={styles.contactFormContainer}>
        <div className={`${styles.contactTitle} font--bold`}>
          Installation Address
          {mode === MODES.EDIT && (
            <button className={styles.editText} onClick={handleOpenModal}>
              Edit
            </button>
          )}
        </div>

        <TextField
          className={styles.textfieldStyles}
          value={installationPoint.address}
          type='text'
          name='address'
          label='Address'
          onChange={() => {}}
          disabled={true}
          InputLabelProps={{
            shrink: Boolean(installationPoint.address), // Automatically shrink the label when value is not empty
          }}
        />
        <TextField
          className={styles.textfieldStyles}
          value={installationPoint.aptOrSuite}
          type='text'
          label='address'
          name='aptOrSuite'
          onChange={() => {}}
          disabled={true}
          InputLabelProps={{
            shrink: Boolean(installationPoint.aptOrSuite), // Automatically shrink the label when value is not empty
          }}
        />
        <TextField
          className={styles.textfieldStyles}
          value={installationPoint.city}
          type='text'
          name='city'
          label='City'
          onChange={() => {}}
          disabled={true}
          InputLabelProps={{
            shrink: Boolean(installationPoint.city), // Automatically shrink the label when value is not empty
          }}
        />
        <div className={styles.zipStateContainer}>
          <TextField
            value={installationPoint.state}
            type='text'
            name='state'
            label='State'
            onChange={() => {}}
            disabled={true}
            InputLabelProps={{
              shrink: Boolean(installationPoint.state), // Automatically shrink the label when value is not empty
            }}
          />
          <TextField
            type='tel'
            value={installationPoint.zipCode}
            name='zipCode'
            label='Zip'
            sx={{ width: 113 }}
            onChange={() => {}}
            disabled={true}
            InputLabelProps={{
              shrink: Boolean(installationPoint.zipCode), // Automatically shrink the label when value is not empty
            }}
          />
        </div>
      </div>
    </div>
  )
}
